import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const RADIAN = Math.PI / 180;

const Users = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userStats, setUserStats] = useState({ pro: 0, free: 0 });
    const [userGrowth, setUserGrowth] = useState([]);

    useEffect(() => {
        fetchUsers();
        fetchUserGrowth();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await api.get('/users');
            const regularUsers = response.data.filter(user => user.role !== 'admin');
            setUsers(regularUsers);
            
            const stats = regularUsers.reduce((acc, user) => {
                if (parseInt(user.completed_tasks) > 5) {
                    acc.pro++;
                } else {
                    acc.free++;
                }
                return acc;
            }, { pro: 0, free: 0 });
            
            setUserStats(stats);
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const fetchUserGrowth = async () => {
        try {
            const response = await api.get('/users/growth');
            setUserGrowth(response.data || []);
        } catch (error) {
            console.error('Error fetching user growth:', error);
            setUserGrowth([]);
        }
    };

    const pieChartData = [
        { name: 'Pro Users', value: userStats.pro },
        { name: 'Free Users', value: userStats.free }
    ];

    const COLORS = ['#FF3366', '#FF6B4D'];

    const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
        const radius = innerRadius + (outerRadius - innerRadius) * 1.3;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text
                x={x}
                y={y}
                fill="#FF3366"
                textAnchor={x > cx ? 'start' : 'end'}
                dominantBaseline="central"
                className="text-sm"
            >
                {`${name} (${(percent * 100).toFixed(0)}%)`}
            </text>
        );
    };

    const formatGrowthData = (data) => {
        let totalUsers = 0;
        return data.map(item => {
            totalUsers += parseInt(item.new_users);
            return {
                date: new Date(item.date).toLocaleDateString(),
                total_users: totalUsers
            };
        });
    };

    const getStatsData = () => {
        const totalUsers = users.length;
        const activeUsers = users.filter(user => {
            const lastLogin = new Date(user.last_login_at || user.created_at);
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            return lastLogin > weekAgo;
        }).length;
        
        const completedTasks = users.reduce((sum, user) => sum + parseInt(user.completed_tasks || 0), 0);
        const averageCompletion = totalUsers > 0 ? Math.round(completedTasks / totalUsers) : 0;

        return [
            { title: 'Total Users', value: totalUsers, icon: '👥' },
            { title: 'Active This Week', value: activeUsers, icon: '🟢' },
            { title: 'Pro Users', value: userStats.pro, icon: '⭐' },
            { title: 'Avg Tasks/User', value: averageCompletion, icon: '📊' },
        ];
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3366]"></div>
            </div>
        );
    }

    return (
        <div className="relative z-10 p-6">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF3366] to-[#FF6B4D]">
                    User Management
                </h1>
                <p className="text-gray-400 mt-2">Monitor and manage your user base</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                {getStatsData().map((stat, index) => (
                    <div key={index} className="bg-gradient-to-br from-[#151515] to-[#0A0A0A] rounded-xl border border-[#FF3366]/10 p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                        <div className="flex flex-col">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 rounded-lg bg-black/20">
                                    <span className="text-[#FF3366] text-xl">{stat.icon}</span>
                                </div>
                                <h3 className="text-sm font-medium text-gray-300">{stat.title}</h3>
                            </div>
                            <div className="flex items-baseline">
                                <p className="text-3xl font-bold text-white">
                                    {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* User Distribution */}
                <div className="bg-gradient-to-br from-[#151515] to-[#0A0A0A] rounded-xl p-6 border border-[#FF3366]/10 shadow-lg">
                    <h2 className="text-xl font-semibold text-white mb-4">User Distribution</h2>
                    <div className="flex justify-center">
                        <div className="w-full h-80">
                            {pieChartData.length > 0 && (
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={pieChartData}
                                            innerRadius={60}
                                            outerRadius={80}
                                            paddingAngle={5}
                                            dataKey="value"
                                            label={CustomLabel}
                                            labelLine={false}
                                        >
                                            {pieChartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>

                {/* User Growth */}
                <div className="bg-gradient-to-br from-[#151515] to-[#0A0A0A] rounded-xl p-6 border border-[#FF3366]/10 shadow-lg">
                    <h2 className="text-xl font-semibold text-white mb-4">User Growth</h2>
                    <div className="w-full h-80">
                        {userGrowth.length > 0 ? (
                            <ResponsiveContainer>
                                <AreaChart data={formatGrowthData(userGrowth)}>
                                    <defs>
                                        <linearGradient id="growthGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#FF3366" stopOpacity={0.3} />
                                            <stop offset="100%" stopColor="#FF3366" stopOpacity={0.05} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                                    <XAxis dataKey="date" stroke="#FF3366" tick={{ fill: '#FF3366', fontSize: 12 }} tickLine={{ stroke: '#FF3366' }} />
                                    <YAxis stroke="#FF3366" tick={{ fill: '#FF3366', fontSize: 12 }} tickLine={{ stroke: '#FF3366' }} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#0A0A0A',
                                            border: '1px solid #FF3366',
                                            borderRadius: '0.5rem',
                                            boxShadow: '0 4px 6px rgba(255, 51, 102, 0.1)'
                                        }}
                                        labelStyle={{ color: '#FF3366' }}
                                        itemStyle={{ color: '#FF3366' }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="total_users"
                                        stroke="#FF3366"
                                        strokeWidth={2}
                                        fill="url(#growthGradient)"
                                        dot={{ fill: '#FF3366', r: 4 }}
                                        activeDot={{ r: 6, stroke: '#FF3366', strokeWidth: 2, fill: '#0A0A0A' }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex items-center justify-center h-full">
                                <p className="text-gray-400">No growth data available</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Users Table */}
            <div className="bg-gradient-to-br from-[#151515] to-[#0A0A0A] rounded-xl overflow-hidden border border-[#FF3366]/10 shadow-lg">
                <div className="p-6">
                    <h2 className="text-xl font-semibold text-white mb-4">Recent Users</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-left text-sm text-gray-400 border-b border-[#FF3366]/10">
                                    <th className="pb-3 font-medium">USER</th>
                                    <th className="pb-3 font-medium">EMAIL</th>
                                    <th className="pb-3 font-medium">ROLE</th>
                                    <th className="pb-3 font-medium">JOINED</th>
                                    <th className="pb-3 font-medium">COMPLETED TASKS</th>
                                    <th className="pb-3 font-medium">LIKED POSTS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.slice(0, 10).map(user => (
                                    <tr key={user.id} className="border-b border-[#FF3366]/5 text-gray-300 hover:bg-[#FF3366]/5 transition-colors">
                                        <td className="py-4">
                                            <div className="flex items-center gap-3">
                                                {user.avatar_url ? (
                                                    <img 
                                                        src={user.avatar_url} 
                                                        alt={user.name} 
                                                        className="w-8 h-8 rounded-full"
                                                    />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] flex items-center justify-center text-white text-sm font-medium">
                                                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                                                    </div>
                                                )}
                                                <span className="font-medium">{user.name}</span>
                                            </div>
                                        </td>
                                        <td className="py-4">{user.email}</td>
                                        <td className="py-4">
                                            <span className="px-2 py-1 rounded-full text-xs bg-[#FF6B4D]/10 text-[#FF6B4D] border border-[#FF6B4D]/20">
                                                {user.role || 'user'}
                                            </span>
                                        </td>
                                        <td className="py-4">{new Date(user.created_at).toLocaleDateString()}</td>
                                        <td className="py-4">
                                            <span className="font-medium">{user.completed_tasks || 0}</span>
                                        </td>
                                        <td className="py-4">
                                            <span className="font-medium">{user.liked_posts || 0}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {users.length === 0 && (
                        <div className="text-center py-12">
                            <div className="text-6xl mb-4">👥</div>
                            <h3 className="text-xl font-semibold text-white mb-2">No users found</h3>
                            <p className="text-gray-400">Users will appear here once they register.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Users; 